import React, { useState, useEffect, useMemo } from "react";
import { X, CreditCard, Loader2 } from "lucide-react";
import { hrService } from "../../services/hrService";
import { MONTHS } from "../../constants/hrConstants";
import { useHR } from "../../context/HRContext";
import toast from "react-hot-toast";

// Razorpay Key ID — supports both CRA (process.env.REACT_APP_RAZORPAY_KEY_ID) and Vite (import.meta.env.VITE_RAZORPAY_KEY_ID)
const RAZORPAY_KEY =
  process.env.REACT_APP_RAZORPAY_KEY_ID ||
  process.env.VITE_RAZORPAY_KEY_ID ||
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_RAZORPAY_KEY_ID) ||
  'rzp_test_Mitv03aBlFFlQ0';

/**
 * Calculates net payout from a PaySetup document, attendance ratio, and deductions.
 * This is the single source of truth for the displayed payout amount.
 *
 * @param {Object|null} paySetup     - PaySetup from backend (or null if not configured)
 * @param {number}      employee     - Employee object (fallback salary source)
 * @param {number}      workingDays
 * @param {number}      presentDays
 * @returns {number} Net payout in Rupees (rounded)
 */
function computeNetPayout(paySetup, employee, workingDays, presentDays) {
  const ratio = workingDays > 0 ? presentDays / workingDays : 1;

  if (paySetup && Array.isArray(paySetup.components) && paySetup.components.length > 0) {
    // ONLY divide by 12 when the pay setup frequency is 'yearly'.
    // A monthly gross of ₹1,50,000 must NOT be divided.
    const isYearly = paySetup.frequency === "yearly";

    const earnings = paySetup.components
      .filter((c) => c.type === "earning")
      .reduce((sum, c) => sum + (isYearly ? c.amount / 12 : c.amount), 0);

    const deductions = paySetup.components
      .filter((c) => c.type === "deduction")
      .reduce((sum, c) => sum + (isYearly ? c.amount / 12 : c.amount), 0);

    // Earnings are pro-rated by attendance ratio; deductions are fixed monthly
    return Math.max(0, Math.round(earnings * ratio - deductions));
  }

  // Fallback: no pay setup — use onboarding salary (always stored as monthly figure)
  return Math.max(0, Math.round(Number(employee?.salary || 0) * ratio));
}

export function PaySalaryModal({ isOpen, onClose, employee, onPaymentSuccess }) {
  const { attendance, leaves, paySetups } = useHR();
  const [loading, setLoading]     = useState(false);
  const [fetchedPaySetup, setFetchedPaySetup] = useState(null);
  const [setupLoaded, setSetupLoaded] = useState(false); // prevents flicker before fetch resolves

  const currentYear  = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-indexed

  const [month, setMonth]           = useState(String(currentMonth));
  const [year, setYear]             = useState(String(currentYear));
  const [workingDays, setWorkingDays] = useState(26);
  const [presentDays, setPresentDays] = useState(26);
  const [leaveDays, setLeaveDays]   = useState(0);

  // Synchronously find pay setup from context if already loaded
  const contextSetup = useMemo(() => {
    if (!employee || !paySetups?.length) return null;
    const empId = String(employee.id || employee._id || "");
    return paySetups.find(
      (s) =>
        String(s.employeeId || "") === empId ||
        String(s.employee?._id || "") === empId ||
        String(s.employee?.id || "") === empId
    ) || null;
  }, [paySetups, employee]);

  // Use fetched setup if available, otherwise context setup
  const activePaySetup = fetchedPaySetup || contextSetup;

  // ── Reset state on open/close ─────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setFetchedPaySetup(null);
      setSetupLoaded(false);
    }
  }, [isOpen]);

  // ── Fetch Pay Setup when modal opens for fresh data ──────────────────────
  useEffect(() => {
    if (!isOpen || !employee) return;
    setLoading(true);
    setSetupLoaded(false);
    hrService.getPaySetupByEmployee(employee.id)
      .then((res) => {
        setFetchedPaySetup(res?.data?.paySetup || null);
      })
      .catch((err) => {
        console.error("Error loading pay setup", err);
        setFetchedPaySetup(null);
      })
      .finally(() => {
        setLoading(false);
        setSetupLoaded(true);
      });
  }, [isOpen, employee]);

  // ── Auto-calculate attendance for the selected period ────────────────────
  useEffect(() => {
    if (!isOpen || !employee) return;

    const empLogs = attendance.filter((a) => {
      if (a.employeeId !== employee.id) return false;
      const [aYear, aMonth] = a.date.split("-");
      return Number(aYear) === Number(year) && Number(aMonth) === Number(month);
    });

    const getWorkingDaysCount = (y, m) => {
      const days = new Date(y, m, 0).getDate();
      let count = 0;
      for (let d = 1; d <= days; d++) {
        if (new Date(y, m - 1, d).getDay() !== 0) count++;
      }
      return count;
    };

    const calculatedWorking = getWorkingDaysCount(Number(year), Number(month));
    const absentCount       = empLogs.filter((a) => a.status === "absent").length;

    let approvedLeaveCount = 0;
    const monthStart = new Date(Number(year), Number(month) - 1, 1);
    const monthEnd   = new Date(Number(year), Number(month),     0);

    leaves.forEach((l) => {
      if (l.employeeId !== employee.id || l.status !== "approved") return;
      const start        = new Date(l.fromDate);
      const end          = l.toDate ? new Date(l.toDate) : start;
      const overlapStart = new Date(Math.max(monthStart, start));
      const overlapEnd   = new Date(Math.min(monthEnd, end));
      if (overlapStart <= overlapEnd) {
        for (let d = new Date(overlapStart); d <= overlapEnd; d.setDate(d.getDate() + 1)) {
          if (d.getDay() !== 0) approvedLeaveCount++;
        }
      }
    });

    const totalLeaveDays = absentCount + approvedLeaveCount;
    setWorkingDays(calculatedWorking);
    setLeaveDays(totalLeaveDays);
    setPresentDays(Math.max(0, calculatedWorking - totalLeaveDays));
  }, [isOpen, employee, month, year, attendance, leaves]);

  // ── Net payout — computed as a pure memo from activePaySetup ──────────────
  const amount = useMemo(
    () => computeNetPayout(activePaySetup, employee, workingDays, presentDays),
    [activePaySetup, employee, workingDays, presentDays]
  );

  // ── Display: base monthly gross ───────────────────────────────────────────
  const baseMonthly = useMemo(() => {
    if (!activePaySetup) return Number(employee?.salary || 0);
    return activePaySetup.frequency === "yearly"
      ? activePaySetup.grossSalary / 12
      : activePaySetup.grossSalary;
  }, [activePaySetup, employee]);

  const isReady = setupLoaded || Boolean(contextSetup);

  // ── Razorpay script loader ────────────────────────────────────────────────
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script   = document.createElement("script");
      script.src     = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload  = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // ── Pay handler ───────────────────────────────────────────────────────────
  const handlePay = async () => {
    if (!RAZORPAY_KEY) {
      toast.error("Razorpay Key is not configured. Set VITE_RAZORPAY_KEY_ID in frontend/.env");
      return;
    }

    setLoading(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway script");
        return;
      }

      // Create order on the backend — backend does NOT return keyId any more
      const res = await hrService.createPaymentOrder({
        employeeId: employee.id,
        month:      Number(month),
        year:       Number(year),
        amount,
        workingDays,
        presentDays,
        leaveDays,
      });

      const orderData = res.data?.data;
      if (!orderData) {
        toast.error("Failed to create payment order");
        return;
      }

      const options = {
        // Key comes from frontend env — NOT from backend response
        key:         RAZORPAY_KEY,
        amount:      orderData.amount,
        currency:    orderData.currency,
        name:        "Verve Kitchens",
        description: `Salary Disbursement — ${employee.name} (${MONTHS[Number(month) - 1]} ${year})`,
        order_id:    orderData.orderId,

        handler: async function (response) {
          // This fires after the user completes payment in the Razorpay popup.
          // We call /verify as an optimistic fast-path; the webhook is authoritative.
          setLoading(true);
          try {
            await hrService.verifyPaymentSignature({
              razorpayOrderId:   response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            toast.success(`Salary of ₹${Number(amount).toLocaleString("en-IN")} disbursed successfully!`);
            onPaymentSuccess?.();
            onClose();
          } catch (verifyErr) {
            // /verify failed — this is okay. The webhook will still mark the payment
            // as captured server-side. Show a soft warning rather than an error.
            console.error("verifyPaymentSignature error:", verifyErr.response?.data);
            const msg = verifyErr.response?.data?.message || "";
            if (msg.includes("already been paid")) {
              toast.success("Payment already recorded successfully.");
              onPaymentSuccess?.();
              onClose();
            } else {
              toast(`Payment processed. Status will sync shortly. (${msg || "Please refresh."})`, { icon: "ℹ️" });
              onClose();
            }
          } finally {
            setLoading(false);
          }
        },

        prefill: {
          name:    orderData.employee.name,
          email:   orderData.employee.email,
          contact: orderData.employee.phone,
        },
        theme: { color: "#2b448f" },
        modal: {
          ondismiss: () => setLoading(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("handlePay error:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Failed to initiate payment");
      setLoading(false);
    }
  };

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">Disburse Salary</h2>
            <p className="text-[12px] text-slate-400 mt-0.5">{employee.name} · ID: {employee.id}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Period selector */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label text-[10.5px] font-bold text-slate-400 uppercase tracking-wide">Month</label>
              <select className="input mt-1 text-[13px]" value={month} onChange={(e) => setMonth(e.target.value)}>
                {MONTHS.map((m, idx) => (
                  <option key={idx} value={String(idx + 1)}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label text-[10.5px] font-bold text-slate-400 uppercase tracking-wide">Year</label>
              <select className="input mt-1 text-[13px]" value={year} onChange={(e) => setYear(e.target.value)}>
                {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Attendance adjustments */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-3">
            <h4 className="text-[11.5px] font-bold text-slate-600 uppercase tracking-wide">Attendance Adjustments</h4>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 font-semibold">Total Days</label>
                <input
                  type="number"
                  className="input py-1 px-2.5 mt-0.5 h-8 text-[12px]"
                  value={workingDays}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setWorkingDays(val);
                    setPresentDays(val - leaveDays);
                  }}
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-semibold">Present Days</label>
                <input
                  type="number"
                  className="input py-1 px-2.5 mt-0.5 h-8 text-[12px]"
                  value={presentDays}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setPresentDays(val);
                    setLeaveDays(workingDays - val);
                  }}
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-semibold">Leaves</label>
                <input
                  type="number"
                  className="input py-1 px-2.5 mt-0.5 h-8 text-[12px]"
                  value={leaveDays}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setLeaveDays(val);
                    setPresentDays(workingDays - val);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Amount Display */}
          <div className="bg-gradient-to-br from-[#2b448f]/5 to-[#2b448f]/10 rounded-xl p-4 border border-[#2b448f]/20 flex justify-between items-center">
            <div>
              <span className="text-[11px] font-semibold text-slate-500">Calculated Net Payout</span>
              {!isReady ? (
                <p className="text-[14px] text-slate-400 mt-1 flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                </p>
              ) : (
                <p className="text-[22px] font-black text-[#2b448f] leading-none mt-1">
                  ₹{amount.toLocaleString("en-IN")}
                </p>
              )}
            </div>
            <div className="text-right text-[10px] text-slate-400">
              <p>Base Monthly: ₹{Number(baseMonthly).toLocaleString("en-IN")}</p>
              <p className="mt-0.5">Frequency: {activePaySetup?.frequency === "yearly" ? "Yearly (÷12)" : "Monthly"}</p>
              {activePaySetup && (
                <p className="mt-0.5 text-slate-300">
                  Gross ₹{Number(activePaySetup.components
                    .filter(c => c.type === "earning")
                    .reduce((s, c) => s + (activePaySetup.frequency === "yearly" ? c.amount / 12 : c.amount), 0)
                  ).toLocaleString("en-IN")} − Ded ₹{Number(activePaySetup.components
                    .filter(c => c.type === "deduction")
                    .reduce((s, c) => s + (activePaySetup.frequency === "yearly" ? c.amount / 12 : c.amount), 0)
                  ).toLocaleString("en-IN")}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <p className="text-[9.5px] text-slate-400 max-w-[200px] leading-tight">
            * Webhook is the authoritative payment source. Status syncs even if browser is closed.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-slate-500 text-[12px] font-semibold hover:bg-slate-100 rounded-lg transition-all"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePay}
              disabled={loading || amount <= 0 || !isReady}
              className="btn-primary flex items-center gap-1.5 px-4 py-2 text-[12px] shadow-sm shadow-[#2b448f]/10 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CreditCard className="w-3.5 h-3.5" />
              )}
              Pay Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
